/**
 * `/admin/reference` — rep firms, products, competitors, and the read-only
 * stage and reason-code tables.
 *
 * **Channel pricing is entered as a markup percentage, never as `net_factor`.**
 * The Admin types `15`; the UI computes `1 / 1.15 = 0.8695652`, and shows both
 * alongside a one-line statement of the convention. Read as a *discount off
 * resale* rather than a *markup on Menlo's price*, the same "15%" is a 2.3%
 * error across the entire funnel — $191,183 on the sample file. `0.8695652` is
 * a derived number nobody thinks in, and a field that accepts it invites a typo
 * in the single most consequential value in the product.
 *
 * It therefore gets **a confirmation step of its own**, stating that the change
 * rescales every net revenue figure from now on, that historical snapshots are
 * not restated, and that the pipeline trend will show a step at today's date.
 * The audit entry is what lets Phase 5 annotate that chart.
 */

import { useState } from 'react';
import {
  LIFECYCLE_STATUSES,
  PRODUCT_FAMILIES,
  REGIONS,
  TECHNOLOGY_TYPES,
  FIRM_TYPES,
} from '@menlo/shared';
import { useApiMutation, useApiQuery } from '@/lib/queries';
import {
  REFERENCE_QUERY_KEY,
  useBusinessUnits,
  useReferenceData,
  type BusinessUnitRef,
} from '@/lib/reference';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { humanize } from '@/lib/format';
import { PartsUpload } from './PartsUpload';

export function AdminReference(): React.ReactElement {
  return (
    <div className="space-y-6">
      <RepFirms />
      <BusinessUnits />
      <Products />
      <Competitors />
      <StagesTable />
      <ReasonCodes />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rep firms — the markup conversion
// ---------------------------------------------------------------------------

interface RepFirmRow {
  id: string;
  name: string;
  firm_type: string;
  region: string;
  net_factor: string;
  markup_pct: number;
  is_active: boolean;
}

function RepFirms(): React.ReactElement {
  const firms = useApiQuery<{ items: RepFirmRow[] }>(
    ['admin', 'rep-firms'],
    '/api/rep-firms?pageSize=200',
  );
  const [editing, setEditing] = useState<RepFirmRow | 'new' | null>(null);

  return (
    <Section
      title="Rep firms and distributors"
      hint="The net factor converts a distributor's resale price into what Menlo books."
      action={
        <Button size="sm" variant="outline" onClick={() => setEditing('new')}>
          Add firm
        </Button>
      }
    >
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted-foreground">
          <tr>
            <th scope="col" className="py-1">
              Name
            </th>
            <th scope="col" className="py-1">
              Type
            </th>
            <th scope="col" className="py-1">
              Region
            </th>
            <th scope="col" className="py-1 text-right">
              Markup
            </th>
            <th scope="col" className="py-1 text-right">
              Net factor
            </th>
            <th scope="col" className="py-1" />
          </tr>
        </thead>
        <tbody>
          {(firms.data?.items ?? []).map((firm) => (
            <tr key={firm.id} className="border-t">
              <td className="py-1">{firm.name}</td>
              <td className="py-1">{humanize(firm.firm_type)}</td>
              <td className="py-1">{firm.region}</td>
              <td className="py-1 text-right tabular-nums">{firm.markup_pct}%</td>
              <td className="py-1 text-right tabular-nums">{firm.net_factor}</td>
              <td className="py-1 text-right">
                <Button size="sm" variant="ghost" onClick={() => setEditing(firm)}>
                  Edit
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing !== null && (
        <RepFirmDialog firm={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
    </Section>
  );
}

function RepFirmDialog({
  firm,
  onClose,
}: {
  firm: RepFirmRow | null;
  onClose: () => void;
}): React.ReactElement {
  const [name, setName] = useState(firm?.name ?? '');
  const [firmType, setFirmType] = useState(firm?.firm_type ?? 'distributor');
  const [region, setRegion] = useState(firm?.region ?? 'AMER');
  const [markup, setMarkup] = useState(String(firm?.markup_pct ?? 15));
  const [confirming, setConfirming] = useState(false);

  const markupNumber = Number(markup);
  const factor = Number.isFinite(markupNumber) ? 1 / (1 + markupNumber / 100) : NaN;
  const changesFactor = firm === null || Math.abs((firm.markup_pct ?? 0) - markupNumber) > 1e-9;

  const save = useApiMutation<Record<string, unknown>, void>(
    () => {
      const body = {
        name: name.trim(),
        firm_type: firmType,
        region,
        // A rep never takes title, so its factor is pinned at 1 and the API
        // refuses a markup for one with a message that says why.
        markup_pct: firmType === 'rep' ? 0 : markupNumber,
      };
      return firm === null
        ? { path: '/api/rep-firms', method: 'POST', body }
        : { path: `/api/rep-firms/${firm.id}`, method: 'PUT', body };
    },
    {
      invalidate: [['admin', 'rep-firms'], REFERENCE_QUERY_KEY],
      onSuccess: onClose,
    },
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={firm === null ? 'New rep firm' : `Edit ${firm.name}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              changesFactor && firmType !== 'rep' ? setConfirming(true) : save.mutate()
            }
            disabled={name.trim() === '' || save.isPending}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Name">
          <Input value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <Select value={firmType} onChange={(event) => setFirmType(event.target.value)}>
              {FIRM_TYPES.map((value) => (
                <option key={value} value={value}>
                  {humanize(value)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Region">
            <Select value={region} onChange={(event) => setRegion(event.target.value)}>
              {REGIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {firmType === 'rep' ? (
          <p className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
            A rep never takes title — Menlo invoices the customer at the resale price and pays
            commission separately — so the net factor is 1 and there is no markup to enter. Netting
            the commission here would understate revenue and double-count the cost.
          </p>
        ) : (
          <>
            <Field
              label="Markup (%)"
              hint="What the distributor adds to Menlo's price. Type 15, not 0.87."
            >
              <Input
                inputMode="decimal"
                value={markup}
                onChange={(event) => setMarkup(event.target.value)}
              />
            </Field>
            <p className="rounded-md bg-muted/50 p-3 text-xs">
              The distributor marks Menlo&rsquo;s price up by {markup || '—'}%, so Menlo books{' '}
              <strong>{Number.isFinite(factor) ? (factor * 100).toFixed(1) : '—'}%</strong> of
              resale — a net factor of{' '}
              <strong>{Number.isFinite(factor) ? factor.toFixed(7) : '—'}</strong>.
              <br />
              <span className="text-muted-foreground">
                A {markup || '—'}% markup is the same fact as a{' '}
                {Number.isFinite(factor) ? ((1 - factor) * 100).toFixed(2) : '—'}% margin off
                resale. Both numbers describe one arrangement; only the markup is what anyone
                negotiates.
              </span>
            </p>
          </>
        )}
      </div>

      {confirming && (
        <Dialog
          open
          size="sm"
          onClose={() => setConfirming(false)}
          title="This rescales every net revenue figure"
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setConfirming(false);
                  save.mutate();
                }}
              >
                Change the factor
              </Button>
            </>
          }
        >
          <ul className="list-disc space-y-2 pl-4 text-sm">
            <li>
              Every net revenue figure for deals through {name || 'this firm'} changes from now on —
              pipeline, weighted value, lifetime value, and the account roll-ups.
            </li>
            <li>
              <strong>Historical snapshots are not restated.</strong> They are point-in-time under
              the factor that was in force, which is the only way a trend line means anything.
            </li>
            <li>
              So the 26-week pipeline trend will show a step at today&rsquo;s date, and the
              week-on-week delta on every tile will be wrong for one week. The change is audited,
              which is what lets that chart be annotated rather than explained away.
            </li>
            <li>
              Closures already recorded keep the factor they were closed at. They are append-only.
            </li>
          </ul>
        </Dialog>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Business units — session 7 part 2
// ---------------------------------------------------------------------------

interface BusinessUnitAdminRow {
  code: string;
  label: string;
  status: 'active' | 'legacy' | 'retired';
  live_part_count: number;
}

const BU_STATUS_VARIANT = { active: 'success', legacy: 'outline', retired: 'outline' } as const;

function BusinessUnits(): React.ReactElement {
  const units = useApiQuery<{ items: BusinessUnitAdminRow[] }>(
    ['admin', 'business-units'],
    '/api/admin/business-units',
  );
  const [confirming, setConfirming] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const retire = useApiMutation<{ retired: { code: string }[] }, void>(
    () => ({ path: '/api/admin/business-units/retire-legacy', method: 'POST' }),
    {
      quiet: true,
      invalidate: [['admin', 'business-units'], REFERENCE_QUERY_KEY],
      onSuccess: () => {
        setConfirming(false);
        setRefusal(null);
      },
      onError: (error) => {
        setConfirming(false);
        setRefusal(error.message);
      },
    },
  );

  const items = units.data?.items ?? [];
  const legacy = items.filter((unit) => unit.status === 'legacy');

  return (
    <Section
      title="Business units"
      hint="A part's business unit decides where it reports. Legacy units are the pre-October 2026 ones; retire them once the parts upload has moved every part off them."
      action={
        legacy.length > 0 ? (
          <Button size="sm" variant="outline" onClick={() => setConfirming(true)}>
            Retire legacy BUs
          </Button>
        ) : undefined
      }
    >
      {refusal !== null && (
        <p
          role="alert"
          className="mb-2 rounded-md border border-destructive/40 bg-red-50 p-2 text-xs text-red-900"
        >
          {refusal}
        </p>
      )}
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted-foreground">
          <tr>
            <th scope="col" className="py-1">
              Business unit
            </th>
            <th scope="col" className="py-1">
              Status
            </th>
            <th scope="col" className="py-1 text-right">
              Live parts
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((unit) => (
            <tr key={unit.code} className="border-t">
              <td className="py-1">{unit.label}</td>
              <td className="py-1">
                <Badge variant={BU_STATUS_VARIANT[unit.status]}>{humanize(unit.status)}</Badge>
              </td>
              <td className="py-1 text-right tabular-nums">{unit.live_part_count}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {confirming && (
        <Dialog
          open
          onClose={() => setConfirming(false)}
          size="sm"
          title="Retire legacy business units?"
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
              <Button onClick={() => retire.mutate()} disabled={retire.isPending}>
                Retire {legacy.map((unit) => unit.label).join(', ')}
              </Button>
            </>
          }
        >
          <p className="text-sm">
            Retired units can no longer be chosen for a part and drop out of every filter. Reports
            and history still show their names. This is refused while any live part still uses one.
          </p>
        </Dialog>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Products and competitors
// ---------------------------------------------------------------------------

interface ProductRow {
  id: string;
  part_number: string;
  family: string;
  business_unit: string;
  description: string | null;
  lifecycle_status: string;
}

function Products(): React.ReactElement {
  const products = useApiQuery<{ items: ProductRow[] }>(
    ['admin', 'products'],
    '/api/admin/reference/products',
  );
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const businessUnits = useBusinessUnits();

  const update = useApiMutation<
    Record<string, unknown>,
    { id: string; body: Record<string, unknown> }
  >(({ id, body }) => ({ path: `/api/admin/reference/products/${id}`, method: 'PUT', body }), {
    invalidate: [['admin', 'products'], ['admin', 'business-units'], REFERENCE_QUERY_KEY],
  });

  const create = useApiMutation<Record<string, unknown>, Record<string, unknown>>(
    (body) => ({ path: '/api/admin/reference/products', method: 'POST', body }),
    { invalidate: [['admin', 'products'], REFERENCE_QUERY_KEY], onSuccess: () => setAdding(false) },
  );

  return (
    <Section
      title="Products"
      hint="Business unit and family are properties of the part number, so every report reaches them through the join."
      action={
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setUploading(true)}>
            Upload CSV
          </Button>
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            Add product
          </Button>
        </div>
      }
    >
      {uploading && <PartsUpload onClose={() => setUploading(false)} />}
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted-foreground">
          <tr>
            <th scope="col" className="py-1">
              Part number
            </th>
            <th scope="col" className="py-1">
              Family
            </th>
            <th scope="col" className="py-1">
              Business unit
            </th>
            <th scope="col" className="py-1">
              Lifecycle
            </th>
            <th scope="col" className="py-1">
              Description
            </th>
          </tr>
        </thead>
        <tbody>
          {(products.data?.items ?? []).map((product) => (
            <tr key={product.id} className="border-t">
              <td className="py-1">
                <InlineText
                  key={`pn-${product.part_number}`}
                  label={`Part number for ${product.part_number}`}
                  className="h-7 w-44 font-mono text-xs"
                  value={product.part_number}
                  required
                  onSave={(value) =>
                    update.mutateAsync({ id: product.id, body: { part_number: value } })
                  }
                />
              </td>
              <td className="py-1">
                <Select
                  aria-label={`Family for ${product.part_number}`}
                  className="h-7 w-44 text-xs"
                  defaultValue={product.family}
                  onChange={(event) =>
                    update.mutate({ id: product.id, body: { family: event.target.value } })
                  }
                >
                  {PRODUCT_FAMILIES.map((value) => (
                    <option key={value} value={value}>
                      {humanize(value)}
                    </option>
                  ))}
                </Select>
              </td>
              <td className="py-1">
                <Select
                  aria-label={`Business unit for ${product.part_number}`}
                  className="h-7 w-40 text-xs"
                  // Controlled: the options arrive with /api/reference, after
                  // mount, and an uncontrolled select would then show the first
                  // option rather than the stored BU.
                  value={product.business_unit}
                  onChange={(event) =>
                    update.mutate({ id: product.id, body: { business_unit: event.target.value } })
                  }
                >
                  <BusinessUnitOptions
                    options={businessUnits.options}
                    current={product.business_unit}
                    label={businessUnits.label}
                  />
                </Select>
              </td>
              <td className="py-1">
                <Select
                  aria-label={`Lifecycle for ${product.part_number}`}
                  className="h-7 w-32 text-xs"
                  defaultValue={product.lifecycle_status}
                  onChange={(event) =>
                    update.mutate({
                      id: product.id,
                      body: { lifecycle_status: event.target.value },
                    })
                  }
                >
                  {LIFECYCLE_STATUSES.map((value) => (
                    <option key={value} value={value}>
                      {humanize(value)}
                    </option>
                  ))}
                </Select>
              </td>
              <td className="py-1">
                <InlineText
                  key={`d-${product.description ?? ''}`}
                  label={`Description for ${product.part_number}`}
                  className="h-7 w-full min-w-[12rem] text-xs"
                  value={product.description ?? ''}
                  onSave={(value) =>
                    update.mutateAsync({
                      id: product.id,
                      body: { description: value === '' ? null : value },
                    })
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {adding && (
        <AddProductDialog
          businessUnits={businessUnits.options.filter((unit) => unit.status === 'active')}
          onClose={() => setAdding(false)}
          onCreate={(body) => create.mutate(body)}
        />
      )}
    </Section>
  );
}

/**
 * A cell edited in place: saved on blur or Enter when it changed, put back on
 * Escape. A required one that is blanked goes back to its stored value, and so
 * does a refused save (a duplicate part number is a 409). The caller keys it by
 * the stored value, so a save from elsewhere re-renders it.
 */
function InlineText({
  label,
  value,
  className,
  required = false,
  onSave,
}: {
  label: string;
  value: string;
  className?: string;
  required?: boolean;
  onSave: (value: string) => Promise<unknown>;
}): React.ReactElement {
  const [draft, setDraft] = useState(value);
  const commit = () => {
    const next = draft.trim();
    if (next === value) return;
    if (required && next === '') {
      setDraft(value);
      return;
    }
    // A refused save (the error toast says why) puts the stored value back.
    onSave(next).catch(() => setDraft(value));
  };
  return (
    <Input
      aria-label={label}
      className={className}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
        if (event.key === 'Escape') setDraft(value);
      }}
    />
  );
}

/**
 * The BU choices for a part: every non-retired BU, legacy ones marked as such.
 * A part's current BU is always listed, so the select never silently shows a
 * different value from the one stored.
 */
function BusinessUnitOptions({
  options,
  current,
  label,
}: {
  options: BusinessUnitRef[];
  current: string;
  label: (code: string) => string;
}): React.ReactElement {
  const listed = options.some((unit) => unit.code === current);
  return (
    <>
      {!listed && <option value={current}>{label(current)}</option>}
      {options.map((unit) => (
        <option key={unit.code} value={unit.code}>
          {unit.status === 'legacy' ? `${unit.label} (legacy)` : unit.label}
        </option>
      ))}
    </>
  );
}

function AddProductDialog({
  businessUnits,
  onClose,
  onCreate,
}: {
  businessUnits: BusinessUnitRef[];
  onClose: () => void;
  onCreate: (body: Record<string, unknown>) => void;
}): React.ReactElement {
  const [partNumber, setPartNumber] = useState('');
  const [family, setFamily] = useState<string>('rf_switch');
  // New parts go on an active BU; legacy ones exist only until the parts
  // upload has moved every part off them.
  const [chosenBusinessUnit, setBusinessUnit] = useState<string>('');
  // Until the Admin picks one, the first active BU — read at render, because
  // the list may arrive after the dialog opens.
  const businessUnit = chosenBusinessUnit || (businessUnits[0]?.code ?? '');

  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title="New product"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              onCreate({
                part_number: partNumber.trim(),
                family,
                business_unit: businessUnit,
              })
            }
            disabled={partNumber.trim() === '' || businessUnit === ''}
          >
            Create
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Part number">
          <Input value={partNumber} onChange={(event) => setPartNumber(event.target.value)} />
        </Field>
        <Field label="Family">
          <Select value={family} onChange={(event) => setFamily(event.target.value)}>
            {PRODUCT_FAMILIES.map((value) => (
              <option key={value} value={value}>
                {humanize(value)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Business unit">
          <Select value={businessUnit} onChange={(event) => setBusinessUnit(event.target.value)}>
            {businessUnits.map((unit) => (
              <option key={unit.code} value={unit.code}>
                {unit.label}
              </option>
            ))}
          </Select>
        </Field>
        <p className="text-xs text-muted-foreground">
          An evaluation board is an ordinary product with its own part number. There is no eval-kit
          module.
        </p>
      </div>
    </Dialog>
  );
}

function Competitors(): React.ReactElement {
  const competitors = useApiQuery<{
    items: { id: string; name: string; technology_type: string }[];
  }>(['admin', 'competitors'], '/api/admin/reference/competitors');
  const [name, setName] = useState('');
  const [technology, setTechnology] = useState<string>('em_relay');

  const create = useApiMutation<Record<string, unknown>, void>(
    () => ({
      path: '/api/admin/reference/competitors',
      method: 'POST',
      body: { name: name.trim(), technology_type: technology },
    }),
    {
      invalidate: [['admin', 'competitors'], REFERENCE_QUERY_KEY],
      onSuccess: () => setName(''),
    },
  );

  return (
    <Section title="Competitors" hint="Named on a loss, and what the win/loss report groups by.">
      <ul className="mb-3 flex flex-wrap gap-1.5">
        {(competitors.data?.items ?? []).map((competitor) => (
          <li key={competitor.id}>
            <Badge variant="outline">
              {competitor.name} — {humanize(competitor.technology_type)}
            </Badge>
          </li>
        ))}
      </ul>
      <div className="flex items-end gap-2">
        <Field label="Name">
          <Input className="w-56" value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label="Technology">
          <Select
            className="w-44"
            value={technology}
            onChange={(event) => setTechnology(event.target.value)}
          >
            {TECHNOLOGY_TYPES.map((value) => (
              <option key={value} value={value}>
                {humanize(value)}
              </option>
            ))}
          </Select>
        </Field>
        <Button size="sm" onClick={() => create.mutate()} disabled={name.trim() === ''}>
          Add
        </Button>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Migration-managed tables
// ---------------------------------------------------------------------------

/**
 * Why there is no Edit button here.
 *
 * An Admin who came to this page looking for a stage to change should learn
 * the reason in the place they looked — not by hunting for a control that does
 * not exist and concluding the screen is unfinished.
 */
function MigrationManagedNotice({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="mb-3 rounded-md border bg-muted/40 p-3 text-xs">
      <p className="font-medium">Changed by a migration, not on this screen.</p>
      <p className="mt-1 text-muted-foreground">
        These rows are behaviour rather than reference data: {children} Changes ship as a migration,
        which means they go through a pull request and are audited by git — a review before the
        change, rather than an audit row after it.
      </p>
    </div>
  );
}

/**
 * `stages` and `reason_codes` are **migration-managed, not UI-editable** (§2),
 * and this screen says so on the page rather than leaving an Admin to infer it
 * from a missing button.
 *
 * The reason is that these two tables are not reference data in the sense the
 * others are — they are *behaviour*. `sort_order` drives two stall predicates
 * and the import precedence rule, `required_fields` drives stage gating, and
 * `channel_aliases` drives import matching. An Admin mistyping `sort_order`
 * would silently stop `stalled_win_not_ramping` firing: no error, no clue, and
 * the only symptom is a risk signal that never appears. Shipping them as
 * migrations puts every change through a PR and leaves it audited by git.
 *
 * `channel_aliases` is shown because it is worth understanding: three of the
 * five channel-visible stage names do not string-match ours, and without the
 * aliases 58 of the 124 real distributor rows are hard import errors.
 */
function StagesTable(): React.ReactElement {
  const reference = useReferenceData();
  return (
    <Section
      title="Stages"
      hint="Managed as migrations, not edited here. Every stage comparison in the product goes through sort_order rather than the code."
    >
      <MigrationManagedNotice>
        <code className="rounded bg-muted px-1">sort_order</code> decides two of the seven stall
        signals and which row wins when an imported group carries two different stages;{' '}
        <code className="rounded bg-muted px-1">required_fields</code> is the stage gate; and{' '}
        <code className="rounded bg-muted px-1">channel_aliases</code> is what matches a
        distributor&rsquo;s vocabulary to ours on import. A typo in any of them fails silently — a
        stall signal that simply stops firing has no error to notice.
      </MigrationManagedNotice>

      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted-foreground">
          <tr>
            <th scope="col" className="py-1">
              Order
            </th>
            <th scope="col" className="py-1">
              Code
            </th>
            <th scope="col" className="py-1">
              Name
            </th>
            <th scope="col" className="py-1">
              Channel aliases
            </th>
            <th scope="col" className="py-1 text-right">
              Probability
            </th>
            <th scope="col" className="py-1 text-right">
              Target days
            </th>
            <th scope="col" className="py-1">
              Required
            </th>
          </tr>
        </thead>
        <tbody>
          {[...reference.stages]
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((stage) => (
              <tr key={stage.code} className="border-t align-top">
                <td className="py-1 tabular-nums">{stage.sort_order}</td>
                <td className="py-1 font-mono text-xs">{stage.code}</td>
                <td className="py-1">{stage.name}</td>
                <td className="py-1 text-xs">
                  {stage.channel_aliases.length === 0 ? (
                    <span className="text-muted-foreground">— internal only</span>
                  ) : (
                    stage.channel_aliases.join(', ')
                  )}
                </td>
                <td className="py-1 text-right tabular-nums">{stage.default_probability}%</td>
                <td className="py-1 text-right tabular-nums">
                  {stage.target_days ?? 'never stalls'}
                </td>
                <td className="py-1 text-xs">
                  {stage.required_fields.length === 0 ? '—' : stage.required_fields.join(', ')}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </Section>
  );
}

function ReasonCodes(): React.ReactElement {
  const reference = useReferenceData();
  return (
    <Section
      title="Reason codes"
      hint="Managed as migrations, not edited here. applies_to carries the four outcome values, so a code can only be chosen for a closure it fits."
    >
      <MigrationManagedNotice>
        <code className="rounded bg-muted px-1">applies_to</code> is validated against every
        closure, so a code whose outcomes are wrong makes a legitimate closure impossible to record
        — and a closure already written cannot be corrected, because the table is append-only.
      </MigrationManagedNotice>

      <ul className="grid gap-1 sm:grid-cols-2">
        {reference.reason_codes.map((code) => (
          <li key={code.code} className="flex items-center justify-between gap-2 text-sm">
            <span>
              {code.label}{' '}
              <span className="font-mono text-[11px] text-muted-foreground">{code.code}</span>
            </span>
            <span className="flex gap-1">
              {code.applies_to.map((outcome) => (
                <Badge key={outcome} variant="outline">
                  {humanize(outcome)}
                </Badge>
              ))}
              {!code.is_active && <Badge variant="risk">not selectable</Badge>}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-muted-foreground">
        <code>IMPORTED_CHANNEL</code> is deliberately not selectable: it exists so the importer can
        record a closure for a deal that arrived already won, and a person must never choose it.
      </p>
    </Section>
  );
}

function Section({
  title,
  hint,
  action,
  children,
}: {
  title: string;
  hint?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="rounded-lg border bg-card p-4">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-medium">{title}</h2>
          {hint !== undefined && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}
